import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowDragEndEvent,
} from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormsContext } from "../../context/FormsContext";
import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import { ErrorModal } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import { INITIAL_VALUES_OPTION } from "../../models/OptionInitialValue";
import type { Option } from "../../models/Option";
import { useSearchQuestionOptions } from "../../hooks/useSearchQuestionOptions";
import { updateOptionSortOrder } from "../../repositories/FormsRepository";
import { DeleteOptionDialog } from "./questions-options-modals/modal-delete-option";
import { NewAndEditOptionModal } from "./questions-options-modals/modal-new-and-edit-option";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import AddBoxIcon from "@material-ui/icons/AddBox";

const useStyles = makeStyles((theme) => ({
	gridContainer: {
		"& .ag-row-drag": {
			cursor: "grab !important",
		},
		"& .ag-row-dragging": {
			opacity: 0.6,
			backgroundColor: "#f5f5f5 !important",
		},
		"& .ag-row-drag-move": {
			cursor: "grabbing !important",
		},
		"& .ag-row.drag-target-above::before": {
			content: "''",
			position: "absolute",
			top: "-1px",
			left: "0",
			right: "0",
			height: "2px",
			backgroundColor: theme.palette.primary.main,
			zIndex: 1000,
			boxShadow: `0 0 3px ${theme.palette.primary.main}99`,
		},
		"& .ag-row.drag-target-below::after": {
			content: "''",
			position: "absolute",
			bottom: "-1px",
			left: "0",
			right: "0",
			height: "2px",
			backgroundColor: theme.palette.primary.main,
			zIndex: 1000,
			boxShadow: `0 0 3px ${theme.palette.primary.main}99`,
		},
	},
	btnAdd: {
		position: "absolute",
		padding: 0,
		bottom: 11,
		left: 18,
	},
	btnAddOption: {
		position: "absolute",
		padding: 0,
		bottom: 16,
		left: 18,
		opacity: 0.5,
		"&.enabled": {
			opacity: 1,
			cursor: "pointer",
		},
		"&.disabled": {
			cursor: "not-allowed",
		},
	},
}));

type Props = {
	questionIdToSearchOptions: number;
	setOpenNewEditOption: (setter: boolean) => void;
	setOpenDeleteOption: (setter: boolean) => void;
	setNewOptionMode: (setter: boolean) => void;
	setShouldUpdateWhenExit: (shouldUpdate: boolean) => void;
	openNewEditOption: boolean;
	openDeleteOption: boolean;
	newOptionMode: boolean;
};

export const OptionsTable = ({
	setShouldUpdateWhenExit,
	questionIdToSearchOptions,
	setOpenNewEditOption,
	openNewEditOption,
	openDeleteOption,
	setOpenDeleteOption,
	newOptionMode,
	setNewOptionMode,
}: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const gridStyle = useMemo(
		() => ({ height: "100%", width: "100%", padding: 10 }),
		[],
	);
	const [optionsColumnDefs, setOptionsColumnDefs] = useState<ColDef[]>([]);
	const classes = useStyles();

	const {
		state: { optionSelectedInTable, questionOptionsList, questionList },
		actions: { setOptionSelectedInTable, setOptionId, setOptionText },
	} = useFormsContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const searchQuestionOptions = useSearchQuestionOptions({
		setIsLoading,
		setError,
		questionIdToSearchOptions,
	});

	const loadingOverlayComponent = useMemo<any>(() => {
		return LazyLoading;
	}, []);

	const onRowDragEnd = async (params: RowDragEndEvent) => {
		if (!params.node?.data?.OptionId || !params.api) {
			const allRows = document.querySelectorAll(".ag-row");
			allRows.forEach((row) => {
				row.classList.remove("drag-target-above", "drag-target-below");
			});
			return;
		}

		let optionIdBelow = null;
		let isDroppingAbove = false;
		let isDroppingBelow = false;

		// Check if we have a target node to determine drop position
		if (params.overNode && params.overNode.data) {
			const targetRowElement = document.querySelector(
				`[row-id="${params.overNode.id}"]`,
			);

			if (targetRowElement) {
				// Read the classes BEFORE cleaning them up
				isDroppingAbove =
					targetRowElement.classList.contains("drag-target-above");
				isDroppingBelow =
					targetRowElement.classList.contains("drag-target-below");

				if (isDroppingAbove) {
					// Insert BEFORE the target row - SP expects the option to insert BEFORE
					optionIdBelow = params.overNode.data.OptionId;
				} else if (isDroppingBelow) {
					// Insert AFTER the target row - find the next row to insert BEFORE
					const targetIndex = params.overNode.rowIndex!;
					const nextRow = params.api.getDisplayedRowAtIndex(targetIndex + 1);
					optionIdBelow =
						nextRow && nextRow.data ? nextRow.data.OptionId : null;
				} else {
					// Fallback - treat as dropping above
					optionIdBelow = params.overNode.data.OptionId;
				}
			}
		}

		const allRows = document.querySelectorAll(".ag-row");
		allRows.forEach((row) => {
			row.classList.remove("drag-target-above", "drag-target-below");
		});

		try {
			const res = await updateOptionSortOrder(
				params.node.data.OptionId,
				optionIdBelow,
			);

			if (res.ok) {
				searchQuestionOptions();
				setShouldUpdateWhenExit(true);
			} else {
				setError(res.message || "Error updating option order");
			}
		} catch (error) {
			setError("Error updating option priority");
		}
	};

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const getOptionsContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: Option = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (data) {
				result.push({
					name: "Edit",
					action: () => {
						setNewOptionMode(false);
						setOptionSelectedInTable(data);
						setOptionId(data.OptionId);
						setOptionText(data.OptionText);
						setOpenNewEditOption(true);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}

			if (data) {
				result.push({
					name: "New Option",
					action: () => {
						setOptionSelectedInTable(INITIAL_VALUES_OPTION);
						setNewOptionMode(true);
						setOptionId(null);
						setOptionText("");
						setOpenNewEditOption(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Delete",
					action: () => {
						setNewOptionMode(false);
						setOpenDeleteOption(true);
						setOptionSelectedInTable(data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			const addNewOption: (DefaultMenuItem | MenuItemDef)[] = [];

			if (!data && questionIdToSearchOptions !== null) {
				addNewOption.push({
					name: "New",
					action: () => {
						setOptionSelectedInTable(INITIAL_VALUES_OPTION);
						setNewOptionMode(true);
						setOptionId(null);
						setOptionText("");
						setOpenNewEditOption(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			return data ? result : addNewOption;
		},
		[questionOptionsList],
	);

	const renderColumns = () => {
		setOptionsColumnDefs([]);
		const newColumns: ColDef[] = [];

		newColumns.push({
			field: "",
			headerName: "",
			rowDrag: true,
			minWidth: 50,
			maxWidth: 50,
			suppressHeaderMenuButton: true,
			suppressHeaderFilterButton: true,
		});

		newColumns.push({
			field: "OptionText",
			headerName: "Option",
			sortable: true,
			flex: 3,
			minWidth: 180,
		});
		newColumns.push({
			field: "IsEnabled",
			headerName: "Is Enabled",
			sortable: false,
			flex: 1,
			minWidth: 130,
			valueFormatter: (params) => (params.value ? "Yes" : "No"),
		}),
			newColumns.push({
				field: "OptionTypeCode",
				headerName: "Option Type",
				sortable: false,
				flex: 1,
				minWidth: 100,
				maxWidth: 100,
				valueFormatter: (params) => {
					switch (params.value) {
						case "O":
							return "OK";
						case "W":
							return "Warning";
						case "A":
							return "Alert";
						default:
							return params.value;
					}
				},
			});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid
							item
							xs={12}
							md={12}
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid item xs={12} md={12}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			},
		});
		setOptionsColumnDefs(newColumns);
	};

	useEffect(() => {
		renderColumns();
	}, [questionList]);

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else gridApi?.hideOverlay();
	}, [isLoading, questionOptionsList, gridApi]);

	useEffect(() => {
		if (questionIdToSearchOptions) searchQuestionOptions();
	}, [questionIdToSearchOptions]);

	return (
		<>
			<Grid
				item
				md={6}
				xs={6}
				style={{
					height: "58vh",
					border: "4px solid #ccc",
					borderRadius: 10,
					position: "relative",
				}}
			>
				<div
					style={{ ...gridStyle, paddingBottom: 60 }}
					className={classes.gridContainer}
				>
					<AgGridReact
						rowData={questionOptionsList}
						columnDefs={optionsColumnDefs}
						defaultColDef={globalDefaultColDef}
						rowHeight={38}
						headerHeight={42}
						loadingOverlayComponent={loadingOverlayComponent}
						animateRows={true}
						getContextMenuItems={(e: any) => getOptionsContextMenuItems(e)}
						pagination={false}
						onRowDoubleClicked={(event) => {
							setNewOptionMode(false);
							setOptionSelectedInTable(event.data);
							setOptionId(event.data?.OptionId);
							setOptionText(event.data?.OptionText);
							setOpenNewEditOption(true);
						}}
						rowSelection="single"
						onGridReady={(params) => setGridApi(params.api)}
						getRowId={(params) => params.data.OptionId}
						onRowDragEnd={onRowDragEnd}
						rowDragEntireRow={true}
						suppressMoveWhenRowDragging={false}
						suppressRowDrag={false}
						onRowDragMove={(params) => {
							const allRows = document.querySelectorAll(".ag-row");
							allRows.forEach((row) => {
								row.classList.remove("drag-target-above", "drag-target-below");
							});

							if (params.overNode) {
								const targetRowElement = document.querySelector(
									`[row-id="${params.overNode.id}"]`,
								);
								if (targetRowElement) {
									const rect = targetRowElement.getBoundingClientRect();
									const mouseY = params.event?.clientY || 0;
									const rowMiddle = rect.top + rect.height / 2;

									if (mouseY < rowMiddle) {
										targetRowElement.classList.add("drag-target-above");
									} else {
										targetRowElement.classList.add("drag-target-below");
									}
								}
							}
						}}
						onRowDragLeave={() => {
							const allRows = document.querySelectorAll(".ag-row");
							allRows.forEach((row) => {
								row.classList.remove("drag-target-above", "drag-target-below");
							});
						}}
					/>
				</div>
				<IconButton
					onClick={() => {
						setOptionSelectedInTable(INITIAL_VALUES_OPTION);
						setNewOptionMode(true);
						setOptionId(null);
						setOptionText("");
						setOpenNewEditOption(true);
					}}
					aria-label="add option"
					color="primary"
					disabled={!questionIdToSearchOptions}
					className={classes.btnAdd}
					style={{
						position: "absolute",
						padding: 0,
						bottom: 16,
						left: 18,
					}}
				>
					<AddBoxIcon style={{ width: 35, height: 35 }} />
				</IconButton>
			</Grid>
			<NewAndEditOptionModal
				show={openNewEditOption}
				data={optionSelectedInTable}
				questionIdToSearchOptions={questionIdToSearchOptions as number}
				newMode={newOptionMode}
				setShouldUpdateWhenExit={setShouldUpdateWhenExit}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchQuestionOptions();
					}
					setOpenNewEditOption(false);
				}}
			/>
			<DeleteOptionDialog
				show={openDeleteOption}
				setShouldUpdateWhenExit={setShouldUpdateWhenExit}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchQuestionOptions();
					}
					setOpenDeleteOption(false);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
